import { ChangeEvent, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Paper from "@material-ui/core/Paper";
import Divider from "@material-ui/core/Divider";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { UseMutationResult } from "@tanstack/react-query";
import TextField from "@material-ui/core/TextField";
import WatchLater from "@material-ui/icons/WatchLater";
import CalendarToday from "@material-ui/icons/CalendarToday";
import ServiceField from "./components/service-field";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Typography from "@material-ui/core/Typography";
import InputMask from "react-input-mask";
import Grid from "@material-ui/core/Grid";
import { handleNumberChange } from "../../../../../../../../../../../../../utils/handleNumberChange";
import { AttendanceLocalDepartment } from "../../../../../../../../../../../../../@types/attendance-local-department";
import { AttendanceLocalDepartmentQueue } from "../../../../../../../../../../../../../@types/attendance-local-department-queue";
import { AttendanceLocalDepartmentQueueServiceTimeException } from "../../../../../../../../../../../../../@types/attendance-local-department-queue-service-time-exception";
import { AttendanceLocalDepartmentQueueServiceTimeExceptionCreateData } from "../../../../../../../../../../../../../services/api-tansquery/attendance-local/department/queue/service-time/exception/create";

type FormProps = {
    queueId: AttendanceLocalDepartmentQueue["id"];
    departmentId: AttendanceLocalDepartment["id"];
    serviceTimeException?: AttendanceLocalDepartmentQueueServiceTimeException;
    mutation?: UseMutationResult<
        AttendanceLocalDepartmentQueueServiceTimeException,
        unknown,
        AttendanceLocalDepartmentQueueServiceTimeExceptionCreateData,
        unknown
    >
};

export default function Form({ queueId, departmentId, serviceTimeException, mutation }: FormProps) {
    const numberFieldProps = { min: 0, max: 100 };
    const history = useHistory();
    const { slug, department_id } = useParams<{ slug: string; department_id: string; }>();
    const { classes } = useFormStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            setor: department_id,
            fila: queueId.toString(),
            servico: serviceTimeException?.servico.id.toString() || "",
            tempo_presencial: "",
            tempo_online: "",
            data: "",
            horario_especial_agendamento: false,
            hora_inicio_agendamento: "",
            hora_fim_agendamento: "",
            intervalo_hora_inicio_agendamento: "",
            intervalo_hora_fim_agendamento: "",
            horario_especial_diario: false,
            hora_inicio_diario: "",
            hora_fim_diario: "",
            intervalo_hora_inicio_diario: "",
            intervalo_hora_fim_diario: ""
        }
    });

    useEffect(() => {
        if (serviceTimeException) {
            form.reset({
                setor: department_id,
                fila: queueId.toString(),
                servico: serviceTimeException.servico.id.toString(),
                tempo_presencial: serviceTimeException.tempo_presencial?.toString() || "",
                tempo_online: serviceTimeException.tempo_online?.toString() || "",
                data: serviceTimeException.data || "",
                horario_especial_agendamento: serviceTimeException.horario_especial_agendamento || false,
                hora_inicio_agendamento: serviceTimeException.hora_inicio_agendamento || "",
                hora_fim_agendamento: serviceTimeException.hora_fim_agendamento || "",
                intervalo_hora_inicio_agendamento: serviceTimeException.intervalo_hora_inicio_agendamento || "",
                intervalo_hora_fim_agendamento: serviceTimeException.intervalo_hora_fim_agendamento || "",
                horario_especial_diario: serviceTimeException.horario_especial_diario || false,
                hora_inicio_diario: serviceTimeException.hora_inicio_diario || "",
                hora_fim_diario: serviceTimeException.hora_fim_diario || "",
                intervalo_hora_inicio_diario: serviceTimeException.intervalo_hora_inicio_diario || "",
                intervalo_hora_fim_diario: serviceTimeException.intervalo_hora_fim_diario || ""
            });
        }
    }, [form, serviceTimeException, queueId, department_id]);

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            setor: Number(department_id),
            fila: queueId,
            servico: Number(data.servico),
            tempo_presencial: Number(data.tempo_presencial),
            tempo_online: Number(data.tempo_online),
            data: data.data.split('/').reverse().join('-'),
            horario_especial_agendamento: data.horario_especial_agendamento,
            hora_inicio_agendamento: data.hora_inicio_agendamento || null,
            hora_fim_agendamento: data.hora_fim_agendamento || null,
            intervalo_hora_inicio_agendamento: data.intervalo_hora_inicio_agendamento || null,
            intervalo_hora_fim_agendamento: data.intervalo_hora_fim_agendamento || null,
            horario_especial_diario: data.horario_especial_diario,
            hora_inicio_diario: data.hora_inicio_diario || null,
            hora_fim_diario: data.hora_fim_diario || null,
            intervalo_hora_inicio_diario: data.intervalo_hora_inicio_diario || null,
            intervalo_hora_fim_diario: data.intervalo_hora_fim_diario || null,
        })
    }

    function preventNonNumericInput(event: React.KeyboardEvent<HTMLInputElement>) {
        if (!/[0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete' && event.key !== 'Tab' && event.key !== 'Enter') {
            event.preventDefault();
        }
    }

    useEffect(() => {
        if (mutation.isSuccess) {
            history.goBack();
        }
    }, [mutation.isSuccess, history]);

    return (
        <Paper
            elevation={3}
            style={{ padding: 20 }}
            component="form"
            onSubmit={form.handleSubmit(handleSubmit)}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} component={Typography} className={classes.title}>
                    Gerenciar Exceção de Horários (Serviço)
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="servico"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <ServiceField
                                field={field}
                                fieldState={fieldState}
                                unidade_slug={slug}
                                setor_unidade={departmentId}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="data"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <InputMask
                                mask="99/99/9999"
                                maskChar={null}
                                value={field.value || ""}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    field.onChange(e.target.value);
                                }}
                            >
                                {(inputProps: any) => (
                                    <TextField
                                        {...inputProps}
                                        required
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        label="Data da Exceção"
                                        inputProps={{ className: classes.textField }}
                                        error={!!fieldState.error}
                                        helperText={
                                            fieldState.error ? fieldState.error.message : ""
                                        }
                                        InputProps={{
                                            required: false,
                                            endAdornment: <CalendarToday htmlColor="#C4C4C4" />
                                        }}
                                    />
                                )}
                            </InputMask>
                        )}
                    />
                </Grid>

                <Grid item xs={6}>
                    <Controller
                        name="tempo_presencial"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                type="number"
                                margin="normal"
                                variant="outlined"
                                label="Tempo de Atendimento Presencial"
                                error={!!fieldState.error}
                                helperText={
                                    fieldState.error ? fieldState.error.message : "Informe o tempo de atendimento presencial"
                                }
                                inputProps={{
                                    ...numberFieldProps,
                                    className: classes.textField,
                                }}
                                InputProps={{
                                    required: false,
                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                }}
                                onKeyPress={preventNonNumericInput}
                                onChange={(e) => {
                                    let value = handleNumberChange(
                                        numberFieldProps.min,
                                        numberFieldProps.max,
                                        e.target.value
                                    );
                                    field.onChange(value.toString());
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={6}>
                    <Controller
                        name="tempo_online"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                type="number"
                                margin="normal"
                                variant="outlined"
                                label="Tempo de Atendimento Online"
                                error={!!fieldState.error}
                                helperText={
                                    fieldState.error ? fieldState.error.message : "Informe o tempo de atendimento online"
                                }
                                inputProps={{
                                    ...numberFieldProps,
                                    className: classes.textField,
                                }}
                                InputProps={{
                                    required: false,
                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                }}
                                onKeyPress={preventNonNumericInput}
                                onChange={(e) => {
                                    let value = handleNumberChange(
                                        numberFieldProps.min,
                                        numberFieldProps.max,
                                        e.target.value
                                    );
                                    field.onChange(value.toString());
                                }}
                            />
                        )}
                    />
                </Grid>

                <Divider style={{ margin: "20px 0" }} />

                <Grid item xs={12} component={Typography}>
                    Configuração de horários
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="horario_especial_agendamento"
                        control={form.control}
                        render={({ field }) => (
                            <FormControl>
                                <FormControlLabel
                                    label="Horário de Agendamento Especial?"
                                    control={<Switch {...field} checked={field.value} />}
                                />
                                <FormHelperText>
                                    Deseja configurar horários especiais para atendimentos agendados?
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </Grid>

                {form.watch("horario_especial_agendamento") && (
                    <>
                        <Grid item xs={6}>
                            <Controller
                                name="hora_inicio_agendamento"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Início do horário de agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de início dos agendamentos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                name="hora_fim_agendamento"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Fim do horário de agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de finalização dos agendamentos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                name="intervalo_hora_inicio_agendamento"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Início do intervalo de agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de início do intervalo para pausa dos agendamentos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="intervalo_hora_fim_agendamento"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Fim do intervalo de agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de finalização do intervalo para pausa dos agendamentos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>
                    </>
                )}

                <Grid item xs={12}>
                    <Controller
                        name="horario_especial_diario"
                        control={form.control}
                        render={({ field }) => (
                            <FormControl>
                                <FormControlLabel
                                    label="Horário de Atendimento Orgânico Especial?"
                                    control={<Switch {...field} checked={field.value} />}
                                />
                                <FormHelperText>
                                    Deseja configurar horários especiais para atendimentos orgânicos (sem agendamento)?
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </Grid>

                {form.watch("horario_especial_diario") && (
                    <>
                        <Grid item xs={6}>
                            <Controller
                                name="hora_inicio_diario"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Início de atendimento orgânico"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de início dos atendimentos orgânico"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <CalendarToday htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="hora_fim_diario"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Fim do atendimento orgânico"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de finalização dos atendimentos orgânico"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <CalendarToday htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="intervalo_hora_inicio_diario"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Início do intervalo do atendimento orgânico"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de início do intervalo para pausa dos atendimentos orgânicos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <CalendarToday htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="intervalo_hora_fim_diario"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Fim do intervalo do atendimento orgânico"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de finalização do intervalo para pausa dos atendimentos orgânicos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <CalendarToday htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>
                    </>
                )}
            </Grid>


            <Box className={classes.footer}>
                <Button
                    className={classes.button}
                    onClick={() => history.goBack()}
                    disabled={mutation.isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    className={classes.saveButton}
                    disabled={mutation.isLoading}
                >
                    {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
            </Box>
        </Paper>
    );
}
